import { Component, Inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { Observable } from "rxjs";

export interface KeepLiveChoiceDialogData {
  kind: "guide" | "guidePack";
  title?: string;
  publishedVersion?: number;
  /** Pre-selects the current choice when the creator changes it later. */
  current?: boolean | null;
}

/** Closes with `true` (keep published version visible), `false` (hide it) or `undefined` (cancelled). */
@Component({
  selector: "app-keep-live-choice-dialog",
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule],
  template: `
    <div class="klc">
      <h2 class="klc__title">While you edit this {{ kindLabel }}…</h2>
      <p class="klc__intro">
        <strong>{{ data.title || "This " + kindLabel }}</strong> is published{{
          data.publishedVersion ? " (v" + data.publishedVersion + ")" : ""
        }}. What should people in your organization see until the new version is approved and published?
      </p>

      <button
        type="button"
        class="klc__option"
        [class.klc__option--selected]="selected === true"
        (click)="selected = true"
      >
        <mat-icon class="klc__icon">visibility</mat-icon>
        <span class="klc__text">
          <span class="klc__label">Keep the published version visible</span>
          <span class="klc__hint">
            People keep finding and using the current version. Your edits stay private until they're
            approved and published.
          </span>
        </span>
      </button>

      <button
        type="button"
        class="klc__option"
        [class.klc__option--selected]="selected === false"
        (click)="selected = false"
      >
        <mat-icon class="klc__icon">visibility_off</mat-icon>
        <span class="klc__text">
          <span class="klc__label">Hide it until the new version is published</span>
          <span class="klc__hint">
            Use this when the current version is wrong or unsafe. It disappears from search and the
            library until the new version is published.
          </span>
        </span>
      </button>

      <p class="klc__footnote">You can change this later from the editor.</p>

      <div class="klc__actions">
        <button mat-button type="button" (click)="dialogRef.close()">Cancel</button>
        <button
          mat-flat-button
          color="primary"
          type="button"
          [disabled]="selected === null || selected === undefined"
          (click)="dialogRef.close(selected)"
        >
          Continue
        </button>
      </div>
    </div>
  `,
  styles: [
    `
      .klc { padding: 20px 22px 14px; max-width: 520px; }
      .klc__title { margin: 0 0 8px; font-size: 19px; font-weight: 600; }
      .klc__intro { margin: 0 0 14px; color: #475569; font-size: 14px; line-height: 1.45; }
      .klc__option {
        display: flex; gap: 12px; align-items: flex-start; width: 100%; text-align: left;
        padding: 12px 14px; margin-bottom: 10px; border: 1px solid #e2e8f0; border-radius: 10px;
        background: #fff; cursor: pointer; font: inherit;
      }
      .klc__option:hover { border-color: #94a3b8; }
      .klc__option--selected { border-color: #2563eb; background: #eff6ff; box-shadow: 0 0 0 1px #2563eb; }
      .klc__icon { color: #2563eb; flex: 0 0 auto; margin-top: 2px; }
      .klc__text { display: flex; flex-direction: column; gap: 3px; }
      .klc__label { font-weight: 600; font-size: 14px; color: #0f172a; }
      .klc__hint { font-size: 13px; color: #64748b; line-height: 1.4; }
      .klc__footnote { margin: 4px 0 0; font-size: 12px; color: #94a3b8; }
      .klc__actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 14px; }
    `,
  ],
})
export class KeepLiveChoiceDialogComponent {
  selected: boolean | null;

  constructor(
    public dialogRef: MatDialogRef<KeepLiveChoiceDialogComponent, boolean | undefined>,
    @Inject(MAT_DIALOG_DATA) public data: KeepLiveChoiceDialogData
  ) {
    this.selected = data?.current ?? true;
  }

  get kindLabel(): string {
    return this.data?.kind === "guidePack" ? "guide pack" : "guide";
  }

  static open(dialog: MatDialog, data: KeepLiveChoiceDialogData): Observable<boolean | undefined> {
    return dialog
      .open<KeepLiveChoiceDialogComponent, KeepLiveChoiceDialogData, boolean | undefined>(
        KeepLiveChoiceDialogComponent,
        { data, width: "min(560px, 96vw)", maxWidth: "96vw", autoFocus: false }
      )
      .afterClosed();
  }
}
